import { useEffect, useMemo, useState } from "react";
import SimplePagination from "./SimplePagination";
import { Button } from "./button";
import { FaRegCalendarCheck } from "react-icons/fa";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "./ui/tooltip";
// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────
export interface PunchRecord {
  date_time: string | null; // "YYYY-MM-DDTHH:MM"
  direction: "Entrada" | "Saída";
  nsr?: string;
}

export interface Employee {
  id?: number;
  pis: string;
  nome: string | null;
  lista_ignorado?: boolean;
  ch_diaria?: number;
  stats_por_mes?: Record<string, { carga_prevista_min: number; carga_cumprida_min: number }>;
  batidas: Record<string, PunchRecord[]>; // key "ddmmYYYY"
}

export interface AfdHeader {
  name?: string;
  identity_number?: string;
  cei?: string;
  registry_start_date?: string; // "ddmmYYYY"
  registry_end_date?: string;
  generation_date?: string;
  generation_time?: { hour: number; minute: number };
  serial_number?: string;
}

export interface AfdData {
  header: AfdHeader;
  employees: Employee[];
  saved_count: number;
  feriados?: FeriadoItem[];
  meta?: {
    current_page: number;
    per_page: number;
    total: number;
    total_pages: number;
    available_months?: string[]; // "YYYY-MM"
  };
}

export interface FeriadoItem {
  id: number;
  nome: string;
  data: string;       // "YYYY-MM-DD"
  tipo: string;
  recorrente: boolean;
}

interface Props {
  data: AfdData;
  onBack: () => void;
  hideHeader?: boolean;
  onPageChange?: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
  onMonthYearChange?: (monthYear: string) => void;
  currentMonthYear?: string;
}

// ─────────────────────────────────────────────────────────────
// Holiday helper
// ─────────────────────────────────────────────────────────────
function buildHolidaySet(feriados: FeriadoItem[], year: number, month: number): Set<string> {
  const set = new Set<string>();
  const mm = String(month).padStart(2, "0");
  feriados.forEach((f) => {
    if (!f.data) return;
    const [fy, fm, fd] = f.data.split("-");
    if (f.recorrente) {
      // match by day+month only, regardless of year
      if (fm === mm) set.add(`${fd}${mm}${year}`);
    } else {
      if (Number(fy) === year && fm === mm) set.add(`${fd}${mm}${year}`);
    }
  });
  return set;
}

function getHolidayName(feriados: FeriadoItem[], dateStr: string, year: number): string | null {
  const dd = dateStr.slice(0, 2);
  const mm = dateStr.slice(2, 4);
  for (const f of feriados) {
    if (!f.data) continue;
    const [fy, fm, fd] = f.data.split("-");
    if (f.recorrente && fm === mm && fd === dd) return f.nome;
    if (!f.recorrente && Number(fy) === year && fm === mm && fd === dd) return f.nome;
  }
  return null;
}

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────
const MONTHS_PT: Record<string, string> = {
  "01": "Janeiro",   "02": "Fevereiro", "03": "Março",    "04": "Abril",
  "05": "Maio",      "06": "Junho",     "07": "Julho",    "08": "Agosto",
  "09": "Setembro",  "10": "Outubro",   "11": "Novembro", "12": "Dezembro",
};

const DAYS_PT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
function fmtMinutes(min: number): string {
  const absMin = Math.abs(Math.round(min));
  const h = Math.floor(absMin / 60);
  const m = absMin % 60;
  const sign = min < 0 ? "-" : "";
  return `${sign}${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function dateStrToYM(s: string): string {
  return `${s.slice(4)}-${s.slice(2, 4)}`;
}

/** Sum minutes from paired punches (even=entry, odd=exit) */
function calcWorkedMinutes(punches: PunchRecord[]): number {
  const sorted = [...punches].sort((a, b) =>
    (a.date_time ?? "").localeCompare(b.date_time ?? "")
  );
  let total = 0;
  for (let i = 0; i + 1 < sorted.length; i += 2) {
    const t1 = new Date(sorted[i].date_time ?? "").getTime();
    const t2 = new Date(sorted[i + 1].date_time ?? "").getTime();
    if (t2 > t1) total += (t2 - t1) / 60000;
  }
  return Math.round(total);
}

// ─────────────────────────────────────────────────────────────
// PunchTable – table rows for one employee / one month
// ─────────────────────────────────────────────────────────────
interface PunchTableProps {
  employee: Employee;
  yearMonth: string; // "YYYY-MM"
  dailyHours: number;
  feriados: FeriadoItem[];
}

function PunchTable({ employee, yearMonth, dailyHours, feriados }: PunchTableProps) {
  const [yr, mo] = yearMonth.split("-").map(Number);
  const daysInMonth = new Date(yr, mo, 0).getDate();
  const dailyMin    = dailyHours * 60;
  const mesNome     = `${MONTHS_PT[yearMonth.split("-")[1]] ?? mo} de ${yr}`;
  const holidaySet  = useMemo(() => buildHolidaySet(feriados, yr, mo), [feriados, yr, mo]);

  const rows = useMemo(() => {
    const out = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const dateObj  = new Date(yr, mo - 1, d);
      const dateStr  = `${String(d).padStart(2, "0")}${String(mo).padStart(2, "0")}${yr}`;
      const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;
      const isHoliday = holidaySet.has(dateStr);
      const punches   = employee.batidas[dateStr] ?? null;
      const sorted    = punches
        ? [...punches].sort((a, b) => (a.date_time ?? "").localeCompare(b.date_time ?? ""))
        : [];
      const workedMin = punches ? calcWorkedMinutes(sorted) : 0;
      out.push({ d, dateObj, dateStr, isWeekend, isHoliday, sorted, workedMin, hasPunches: sorted.length > 0 });
    }
    return out;
  }, [employee.batidas, yr, mo, daysInMonth, holidaySet]);

  return (
    <div className="p-4 text-center">
      {/* <p className="text-lg font-semibold text-gray-600 mb-3 capitalize">{mesNome}</p> */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-blue-50 text-gray-600 text-center">
              {["Data", "Dia", "Entrada", "Saída", "Entrada", "Saída",
                "Ch. Prevista", "Ch. Cumprida", "Saldo do Dia", "Obs."].map((h, i) => (
                <th key={i} className="border border-gray-200 px-2 py-1.5 whitespace-nowrap font-semibold">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ d, dateObj, dateStr, isWeekend, isHoliday, sorted, workedMin, hasPunches }) => {
              const dayName  = DAYS_PT[dateObj.getDay()];
              // Feriados: carga prevista 0, sem falta, sem saldo negativo
              const isExempt = isWeekend || isHoliday;
              const saldoMin = !isExempt ? (hasPunches ? workedMin - dailyMin : 0 - dailyMin) : null;
              const slots    = [0, 1, 2, 3].map(i => sorted[i]?.date_time?.slice(11, 16) ?? null);
              const holidayName = isHoliday ? getHolidayName(feriados, dateStr, yr) : null;
              const diaAtual = new Date()

              let rowClass = "";
              if (isHoliday) rowClass = "bg-amber-50";
              else if (isWeekend) rowClass = "bg-gray-50 text-gray-400";
              else if (!hasPunches) rowClass = "bg-red-50";

              return (
                <tr key={d} className={rowClass}>
                  {/* Data */}
                  <td className="border border-gray-200 px-2 py-1 whitespace-nowrap">
                    {`${String(d).padStart(2, "0")}/${String(mo).padStart(2, "0")}/${yr}`}
                  </td>
                  {/* Dia */}
                  <td className="border border-gray-200 px-2 py-1 text-center">{dayName}</td>
                  {/* 4 time slots */}
                  {slots.map((t, i) => (
                    <td key={i} className="border border-gray-200 px-2 py-1 text-center">
                      {t ? t : <span className="text-gray-300">--</span>}
                    </td>
                  ))}
                  {/* Ch. Prevista */}
                  <td className="border border-gray-200 px-2 py-1 text-center text-gray-500">
                    {isExempt ? "-" : `${String(dailyHours).padStart(2, "0")}:00`}
                  </td>
                  {/* Ch. Cumprida */}
                  <td className="border border-gray-200 px-2 py-1 text-center font-medium">
                    {isExempt ? (
                      <span className="text-gray-300">-</span>
                    ) : (
                      fmtMinutes(workedMin)
                    )}
                  </td>
                  {/* Saldo do Dia */}
                  <td className="border border-gray-200 px-2 py-1 text-center font-semibold">
                    {isExempt || saldoMin === null ? (
                      <span className="text-gray-300">-</span>
                    ) : (
                      <span style={{ color: saldoMin < 0 ? "#ef4444" : saldoMin > 0 ? "#22c55e" : "#3b82f6" }}>
                        {fmtMinutes(saldoMin)}
                      </span>
                    )}
                  </td>
                  {/* Obs */}
                  <td className="border border-gray-200 px-2 py-1 text-center">
                    {isHoliday ? (
                      <span className="text-amber-600 font-medium" title={holidayName ?? undefined}>
                        Feriado
                      </span>
                    ) : !isWeekend && !hasPunches && diaAtual > dateObj ? (
                      <span className="text-red-500 font-medium">Falta</span>
                    ) : ""}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface PunchDialogProps {
  employee: Employee;
  yearMonth: string;
  feriados: FeriadoItem[];
  afdHeader: AfdHeader;
  onClose: () => void;
}

function PunchDialog({ employee, yearMonth, feriados, afdHeader, onClose }: PunchDialogProps) {
  const [moStr, moNum] = [yearMonth.split("-")[1], Number(yearMonth.split("-")[1])];
  const yr = Number(yearMonth.split("-")[0]);
  const mesNome = `${MONTHS_PT[moStr] ?? moStr} de ${yr}`;
  const nome = employee.nome ?? `Funcionário ${employee.pis}`;

  function handlePrint() {
    const win = window.open("", "_blank", "width=950,height=720");
    if (!win) return;

    const dailyHours = employee.ch_diaria ?? 8;
    const dailyMin = dailyHours * 60;
    const daysInMonth = new Date(yr, moNum, 0).getDate();
    const holidaySet = buildHolidaySet(feriados, yr, moNum);

    let rows = "";
    for (let d = 1; d <= daysInMonth; d++) {
      const dateObj  = new Date(yr, moNum - 1, d);
      const dateStr  = `${String(d).padStart(2, "0")}${String(moNum).padStart(2, "0")}${yr}`;
      const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;
      const isHoliday = holidaySet.has(dateStr);
      const punches   = employee.batidas[dateStr] ?? null;
      const sorted    = punches
        ? [...punches].sort((a, b) => (a.date_time ?? "").localeCompare(b.date_time ?? ""))
        : [];
      const workedMin  = punches ? calcWorkedMinutes(sorted) : 0;
      const hasPunches = sorted.length > 0;
      const isExempt   = isWeekend || isHoliday;
      const saldoMin   = !isExempt ? (hasPunches ? workedMin - dailyMin : -dailyMin) : null;
      const slots      = [0, 1, 2, 3].map(i => sorted[i]?.date_time?.slice(11, 16) ?? "--");
      const dayName    = DAYS_PT[dateObj.getDay()];
      const holidayName = isHoliday ? (getHolidayName(feriados, dateStr, yr) ?? "Feriado") : null;

      let rowStyle = "";
      if (isHoliday)       rowStyle = "background:#fffbeb";
      else if (isWeekend)  rowStyle = "background:#f9fafb;color:#888";
      else if (!hasPunches) rowStyle = "background:#fff5f5";

      const saldoCell = isExempt || saldoMin === null
        ? "-"
        : `<span style="color:${saldoMin < 0 ? "#e53e3e" : saldoMin > 0 ? "#16a34a" : "#2563eb"}">${fmtMinutes(saldoMin)}</span>`;

      const obsCell = isHoliday
        ? `<span style="color:#d97706">${holidayName}</span>`
        : (!isWeekend && !hasPunches ? `<span style="color:#e53e3e">Falta</span>` : "");

      rows += `<tr style="${rowStyle}">
        <td>${String(d).padStart(2, "0")}/${String(moNum).padStart(2, "0")}/${yr}</td>
        <td>${dayName}</td>
        <td>${slots[0]}</td><td>${slots[1]}</td><td>${slots[2]}</td><td>${slots[3]}</td>
        <td style="color:#555">${isExempt ? "-" : `${String(dailyHours).padStart(2, "0")}:00`}</td>
        <td style="font-weight:600">${isExempt ? "-" : fmtMinutes(workedMin)}</td>
        <td>${saldoCell}</td>
        <td>${obsCell}</td>
      </tr>`;
    }

    win.document.write(`<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="UTF-8"/>
<title>Relatório de Pontos – ${nome}</title>
<style>
  *{box-sizing:border-box}
  body{font-family:Arial,sans-serif;font-size:11px;color:#222;padding:16px;margin:0}
  .hdr{border-bottom:2px solid #2563eb;padding-bottom:10px;margin-bottom:14px}
  .hdr h1{font-size:17px;margin:0 0 3px;color:#1e40af}
  .hdr h2{font-size:12px;margin:0;color:#555;font-weight:normal}
  .info{display:flex;gap:16px;flex-wrap:wrap;background:#eff6ff;padding:8px 12px;border-radius:4px;margin-bottom:14px}
  .info dl{margin:0}.info dt{font-size:9px;text-transform:uppercase;color:#888}
  .info dd{margin:0;font-weight:bold}
  table{width:100%;border-collapse:collapse;font-size:10px}
  th{background:#dbeafe;padding:4px 6px;text-align:center;border:1px solid #bfdbfe;font-weight:bold;white-space:nowrap}
  td{padding:3px 6px;border:1px solid #e5e7eb;text-align:center;white-space:nowrap}
  @media print{@page{size:A4 landscape;margin:10mm}body{padding:0}}
</style></head><body>
<div class="hdr">
  <h1>${afdHeader.name ?? "Previ - Mossoró"}</h1>
  <h2>Relatório de Controle de Ponto</h2>
</div>
<div class="info">
  <div><dl><dt>Funcionário</dt><dd>${nome}</dd></dl></div>
  <div><dl><dt>PIS</dt><dd>${employee.pis}</dd></dl></div>
  <div><dl><dt>Período</dt><dd>${mesNome}</dd></dl></div>
  ${afdHeader.identity_number ? `<div><dl><dt>CNPJ</dt><dd>${afdHeader.identity_number}</dd></dl></div>` : ""}
  <div><dl><dt>Emitido em</dt><dd>${new Date().toLocaleString("pt-BR")}</dd></dl></div>
</div>
<table>
  <thead><tr>
    <th>Data</th><th>Dia</th><th>Entrada</th><th>Saída</th><th>Entrada</th><th>Saída</th>
    <th>Ch. Prevista</th><th>Ch. Cumprida</th><th>Saldo</th><th>Obs.</th>
  </tr></thead>
  <tbody>${rows}</tbody>
</table>
</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 300);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center  bg-opacity-30 "
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl mx-4 flex flex-col max-h-[90vh] p-6">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-800">{nome}</h2>
            <p className="text-xs text-gray-500 mt-0.5 capitalize"> PIS: {employee.pis}</p>
            <p className="text-xs text-gray-500 mt-0.5 capitalize">Registro do ponto: {mesNome}</p>
          </div>
          <div className="flex items-center gap-3">
           
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
               <p className="text-[16px] font-semibold text-gray-600 text-center">{mesNome}</p>
        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1">
          <PunchTable
            employee={employee}
            yearMonth={yearMonth}
            dailyHours={employee.ch_diaria ?? 8}
            feriados={feriados}
          />
        </div>
       <footer className="px-6 py-4 border-t border-gray-200 flex justify-end items-center gap-3 flex-shrink-0">
          <Button 
            onClick={onClose} 
            type="button"
            className="flex items-center h-9 gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors justify-center"
          >
            Fechar
          </Button>
          
          <button
            type="button"
            onClick={handlePrint}
            className="flex items-center h-9 gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors justify-center cursor-pointer"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Imprimir
          </button>
      </footer>
      </div>
    </div>
  );
}

interface EmployeeTableRowProps {
  index: number;
  employee: Employee;
  selectedYM: string | null;
  feriados: FeriadoItem[];
  afdHeader: AfdHeader;
}

function EmployeeTableRow({ index: _index, employee, selectedYM, feriados, afdHeader }: EmployeeTableRowProps) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const stats = selectedYM ? employee.stats_por_mes?.[selectedYM] : null;
  const nome  = employee.nome ?? `Funcionário ${employee.pis}`;
  const [moStr, yrStr] = selectedYM ? [selectedYM.split("-")[1], selectedYM.split("-")[0]] : ["", ""];
  const periodoLabel = selectedYM ? `${MONTHS_PT[moStr] ?? moStr}/${yrStr}` : "-";

  return (
    <>
      <tr className="hover:bg-gray-50 transition-colors">
        <td className="px-4 py-3 font-medium text-gray-800">{nome}</td>
        <td className="px-4 py-3 text-xs text-gray-500">{employee.pis}</td>
        <td className="px-4 py-3 text-gray-600 text-center">{periodoLabel}</td>
        <td className="px-4 py-3 text-center font-medium text-gray-700">
          {stats ? fmtMinutes(stats.carga_prevista_min) : <span className="text-gray-300">-</span>}
        </td>
        <td className="px-4 py-3 text-center font-medium text-gray-700">
          {stats ? fmtMinutes(stats.carga_cumprida_min) : <span className="text-gray-300">-</span>}
        </td>
        <td className="px-4 py-3 text-center font-medium text-gray-700">
          {stats ? (
            <span style={{ color:
              stats.carga_cumprida_min === 0 ? '#ef4444' :
              (stats.carga_cumprida_min - stats.carga_prevista_min) < 0 ? '#ef4444' :
              (stats.carga_cumprida_min - stats.carga_prevista_min) > 0 ? '#22c55e' :
              '#3b82f6' }}>
              {fmtMinutes(stats.carga_prevista_min - stats.carga_cumprida_min)}
            </span>
          ) : <span className="text-gray-300">-</span>}
        </td>

<td className="px-4 py-3 text-center flex items-center justify-center">
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          disabled={!selectedYM}
          onClick={() => setDialogOpen(true)}
          className="text-blue-700 border-blue-200 hover:bg-blue-600 hover:text-white transition-colors text-xs font-bold cursor-pointer w-8 h-8 p-0 flex items-center justify-center rounded shadow-sm" 
        >
          <FaRegCalendarCheck />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Visualizar Ponto</TooltipContent>
    </Tooltip>
  </TooltipProvider>
</td>
      </tr>
      {dialogOpen && selectedYM && (
        <PunchDialog
          employee={employee}
          yearMonth={selectedYM}
          feriados={feriados}
          afdHeader={afdHeader}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Main AfdResults component
// ─────────────────────────────────────────────────────────────
export default function AfdResults({ data, onBack, hideHeader = false, onPageChange, onPerPageChange, onMonthYearChange, currentMonthYear }: Props) {
  const { header, employees, saved_count, feriados = [] } = data;
  const isServerPaginated = !!onPageChange;

  // Compute sorted unique year-months – prefer server-provided list when paginating
  const allYMs = useMemo(() => {
    if (isServerPaginated && data.meta?.available_months?.length) {
      return data.meta.available_months;
    }
    const s = new Set<string>();
    employees.forEach(emp =>
      Object.keys(emp.batidas).forEach(ds => { if (ds.length === 8) s.add(dateStrToYM(ds)); })
    );
    return [...s].sort();
  }, [employees, isServerPaginated, data.meta?.available_months]);

  const availableMonths = useMemo(
    () => [...new Set(allYMs.map(ym => ym.split("-")[1]))].sort(),
    [allYMs]
  );
  const availableYears = useMemo(
    () => [...new Set(allYMs.map(ym => ym.split("-")[0]))].sort(),
    [allYMs]
  );

  const lastYM = allYMs[allYMs.length - 1] ?? "";
  const initYM  = currentMonthYear || lastYM;
  const [selectedMonth, setSelectedMonth] = useState(initYM.split("-")[1] ?? "");
  const [selectedYear,  setSelectedYear]  = useState(initYM.split("-")[0] ?? "");
  const [searchQuery,   setSearchQuery]   = useState("");

  const selectedYM = selectedYear && selectedMonth ? `${selectedYear}-${selectedMonth}` : null;

  // On first mount only: notify parent of the initial month when it hasn't been set yet
  useEffect(() => {
    if (!currentMonthYear && selectedYM && onMonthYearChange) onMonthYearChange(selectedYM);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMonthChange = (m: string) => {
    setSelectedMonth(m);
    const ym = selectedYear && m ? `${selectedYear}-${m}` : null;
    if (ym && onMonthYearChange) onMonthYearChange(ym);
  };

  const handleYearChange = (y: string) => {
    setSelectedYear(y);
    const ym = y && selectedMonth ? `${y}-${selectedMonth}` : null;
    if (ym && onMonthYearChange) onMonthYearChange(ym);
  };

  // ── Pagination state (client-side fallback) ──
  const [empPage, setEmpPage]       = useState(1);
  const [empPerPage, setEmpPerPage] = useState(10);

  // When server paginating, use meta values; otherwise use local state
  const activePage    = isServerPaginated ? (data.meta?.current_page ?? 1)  : empPage;
  const activePerPage = isServerPaginated ? (data.meta?.per_page     ?? 10) : empPerPage;

  const filteredEmployees = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter(emp =>
      (emp.nome ?? "").toLowerCase().includes(q) ||
      emp.pis.includes(q)
    );
  }, [employees, searchQuery]);

  // Server mode: data already contains only the current page – no slicing needed
  const pagedEmployees = useMemo(() => {
    if (isServerPaginated) return filteredEmployees;
    const start = (empPage - 1) * empPerPage;
    return filteredEmployees.slice(start, start + empPerPage);
  }, [filteredEmployees, empPage, empPerPage, isServerPaginated]);

  return (
    <div>

   <main className="px-4">
    <div className="max-w-6xl mx-auto space-y-2">
      <div className="p-6 bg-gray-50 rounded shadow-sm border border-gray-100">

        <header className="">
              <div className="flex items-center justify-between mb-2">
                <h1 className="text-2xl font-bold text-gray-800 tracking-tight">
                  Sistema de Gestão de Ponto Eletrônico
                </h1>
                {!hideHeader && (
                  <button
                    type="button"
                    onClick={onBack}
                    className="text-sm text-blue-600 border border-blue-300 rounded-lg px-4 py-1.5 hover:bg-blue-50 transition-colors no-print"
                  >
                    + Novo Upload
                  </button>
                )}
              </div>
            <div className="flex items-start gap-3 bg-blue-50/50 border-l-4 border-blue-400 p-4 rounded-r-lg">  
              <div className="flex-shrink-0 mt-0.5">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              
              <p className="text-sm text-slate-600 leading-relaxed">
                <span className="font-semibold text-slate-700 block md:inline mb-1 md:mb-0">
                 Sistema de Gestão de Ponto Eletrônico:
                </span> 
                {" "}Exiba e gerencie os colaboradores cadastrados. Você pode visualizar os detalhes de cada funcionário, incluindo suas batidas de ponto diárias, horas trabalhadas e saldo mensal. Use os filtros para selecionar o período desejado e imprimir relatórios personalizados.
              </p>
            </div>         

            {/* ── Seção de Filtros ── */}
            <div className="no-print bg-gray-50 p-5 rounded-lg border border-gray-200 mt-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xs font-bold text-gray-600 uppercase tracking-widest flex items-center">              
                  Filtros e Impressão
                </h2>
              </div>

              <div className="grid grid-cols-1 md:flex md:items-end gap-4">
                {/* Mês */}
                <div className="flex-1 min-w-[120px]">
                  <label className="block text-[11px] font-semibold text-gray-400 uppercase mb-1.5 ml-1">
                    Mês
                  </label>
                  <select
                    value={selectedMonth}
                    onChange={e => handleMonthChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white
                              focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                  >
                  
                    {availableMonths.map(m => (
                      <option key={m} value={m}>{MONTHS_PT[m] ?? m}</option>
                    ))}
                  </select>
                </div>

                {/* Ano */}
                <div className="flex-1 min-w-[100px]">
                  <label className="block text-[11px] font-semibold text-gray-400 uppercase mb-1.5 ml-1">
                    Ano
                  </label>
                  <select
                    value={selectedYear}
                    onChange={e => handleYearChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white
                              focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                  >
                  
                    {availableYears.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>

                {/* Busca */}
                <div className="flex-[2] min-w-[240px]">
                  <label className="block text-[11px] font-semibold text-gray-400 uppercase mb-1.5 ml-1">
                    Funcionário (Nome ou PIS)
                  </label>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => { setSearchQuery(e.target.value); setEmpPage(1); }}
                    placeholder="Ex: João Silva ou 123.456..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white
                    focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                  />
                </div>

                {/* Ações / Imprimir */}
               
              </div>
            </div>

        </header>

      </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {pagedEmployees.length === 0 ? (
              <div className="py-16 text-center">
                <p className="text-gray-400 text-sm">Nenhum funcionário encontrado.</p>
              </div>
            ) : (
              <>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-blue-50 text-gray-600 text-left">
                      <th className="px-4 py-3 font-semibold border-b border-gray-200">Nome</th>
                      <th className="px-4 py-3 font-semibold border-b border-gray-200 text-center">PIS</th>
                      <th className="px-4 py-3 font-semibold border-b border-gray-200 text-center">Período</th>
                      <th className="px-4 py-3 font-semibold border-b border-gray-200 text-center">Ch. Prevista</th>
                      <th className="px-4 py-3 font-semibold border-b border-gray-200 text-center">Ch. Cumprida</th>
                      <th className="px-4 py-3 font-semibold border-b border-gray-200 text-center">Saldo</th>
                      <th className="px-4 py-3 font-semibold border-b border-gray-200 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <script src="https://cdn.jsdelivr.net/npm/flowbite@2.5.1/dist/flowbite.min.js"></script>
                    {pagedEmployees.map((emp, idx) => (
                      <EmployeeTableRow
                        key={emp.pis}
                        index={(activePage - 1) * activePerPage + idx + 1}
                        employee={emp}
                        selectedYM={selectedYM}
                        feriados={feriados}
                        afdHeader={header}
                      />
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>

          {/* ── Pagination ── */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 no-print mt-4">
            {isServerPaginated ? (
              <SimplePagination
                totalItems={data.meta?.total ?? filteredEmployees.length}
                itemsPerPage={activePerPage}
                currentPage={activePage}
                onPageChange={onPageChange!}
                onPerPageChange={n => { onPerPageChange?.(n); }}
                perPageOptions={[10, 25, 50]}
              />
            ) : (
              <SimplePagination
                totalItems={filteredEmployees.length}
                itemsPerPage={empPerPage}
                currentPage={empPage}
                onPageChange={setEmpPage}
                onPerPageChange={n => { setEmpPerPage(n); setEmpPage(1); }}
                perPageOptions={[10, 25, 50]}
              />
            )}
          </div>

          {/* ── Saved notification ── */}
          {saved_count > 0 && (
            <p className="text-xs text-green-600 no-print">
              ✓ {saved_count} registros salvos no banco de dados.
            </p>
          )}

         

        </div>
      </main>
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 10mm; }
          .no-print  { display: none !important; }
          .print-show { display: block !important; }
          body { font-size: 11px; background: white; }
          header, footer { display: none !important; }
        }
        .print-show { display: none; }
      `}</style>
    </div>
  );
}
